import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as LaunchIcon,
  Article as ReportIcon,
  History as PriorIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';
import { pushError } from '../../stores/notificationStore';
import { ReportViewer } from '../reports/ReportViewer';
import { useUIStore } from '../../stores/uiStore';
import { useAuth } from '../auth/useAuth';
import { useResizableSplitter } from '../../hooks/useResizableSplitter';
import type { Study, DicomObject, BritProtocol, BritWindowContent } from '../../api/types';
import { useViewerProvider } from './ViewerContext';
import { useBestProtocol } from './protocols/useBestProtocol';
import { onOhifReady, sendProtocolToOhif } from './protocols/ohifBridge';
import { distributeProtocol } from './protocols/protocolDistributor';
import { isMultiScreenBritProtocol, britToOhifProtocol } from './protocols/britToOhif';
import { useMonitorManager, PopOutButton, useMonitorConfigStore } from '../multi-monitor';
import { findScreenByLabel } from '../multi-monitor/screenUtils';

interface ViewerTabProps {
  study: Study;
  onClose: () => void;
}

/**
 * OHIF Viewer tab — embeds OHIF v3 via iframe.
 *
 * Lifecycle:
 *   - On mount: call api.openStudy(study.uid) to register viewer session
 *   - On unmount / close: call api.closeStudy(study.uid)
 *   - beforeunload: send closeStudy via navigator.sendBeacon fallback
 *
 * Layout:
 *   ┌──────────────────────────────────┬──────────────────┐
 *   │ Title bar (32px)                 │                  │
 *   ├──────────────────────────────────┤  Prior Studies   │
 *   │                                  │  sidebar (opt.)  │
 *   │  OHIF Viewer (iframe)            │  200px           │
 *   │                                  │                  │
 *   ├──────────────────────────────────┴──────────────────┤
 *   │ Report Panel (collapsible, resizable)               │
 *   └────────────────────────────────────────────────────┘
 */
export const ViewerTab = ({ study, onClose }: ViewerTabProps) => {
  // Backend expects bare site name (e.g. "ISO_PACS"), but study.site has "site:" prefix
  const viewer = useViewerProvider();
  const bestProtocol = useBestProtocol(study);
  const { capability, screens, childWindows, openViewerOnScreen, manager } = useMonitorManager();
  const siteId = (study.siteId ?? study.site ?? 'default').replace(/^site:/, '');
  const sessionId = localStorage.getItem('brit_sid') ?? '';
  const { data: objectResult } = useQuery({
    queryKey: queryKeys.objects(study.objKey),
    queryFn: () => api.getObjects(study.objKey),
    enabled: !!study.objKey && viewer.id !== 'gwt' && !bestProtocol,
    staleTime: 60_000,
  });
  const fallbackProtocol = useMemo(
    () => buildAutoViewerProtocol(study, objectResult?.records),
    [study, objectResult?.records],
  );
  const bridgeProtocol = bestProtocol ?? fallbackProtocol;
  const autoWindowLevels = useMemo(
    () => buildAutoWindowLevels(study, objectResult?.records),
    [study, objectResult?.records],
  );
  const viewerConfig = viewer.buildViewerConfig({ study, siteId, sessionId, protocol: bestProtocol ?? undefined });
  const ohifUrl = viewerConfig.url ?? '';
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const autoWlAppliedRef = useRef<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [priorsOpen, setPriorsOpen] = useState(false);
  const { user } = useAuth();

  // ── Send matched protocol to OHIF once it signals ready ────────
  useEffect(() => {
    if (!bridgeProtocol) return;
    const unsubscribe = onOhifReady(() => {
      const iframe = iframeRef.current;
      if (iframe) {
        sendProtocolToOhif(iframe, bridgeProtocol);
      }
    });
    return unsubscribe;
  }, [bridgeProtocol]);

  useEffect(() => {
    if (!iframeLoaded) return;
    if (autoWindowLevels.length === 0) return;

    const applyKey = `${viewer.id}:${study.uid}`;
    if (autoWlAppliedRef.current === applyKey) return;

    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const postAll = () => {
      const target = iframeRef.current?.contentWindow;
      if (!target) return;
      for (const wl of autoWindowLevels) {
        target.postMessage(
          {
            type: 'BRIT_SET_WL',
            seriesInstanceUID: wl.seriesInstanceUID,
            windowCenter: wl.windowCenter,
            windowWidth: wl.windowWidth,
          },
          '*',
        );
      }
    };

    const schedulePosts = (delays: number[]) => {
      const timers = delays.map(delay => window.setTimeout(postAll, delay));
      autoWlAppliedRef.current = applyKey;
      return () => timers.forEach(timer => window.clearTimeout(timer));
    };

    if (viewer.id === 'gwt') {
      return schedulePosts([500, 1400, 2800, 5000]);
    }

    const unsubscribe = onOhifReady(() => {
      postAll();
    });
    const cleanupTimers = schedulePosts([300, 1200, 2600, 5000]);
    return () => {
      unsubscribe();
      cleanupTimers();
    };
  }, [iframeLoaded, autoWindowLevels, viewer.id, study.uid]);

  // Store close params in a ref for beforeunload (can't use hooks there)
  const closeParamsRef = useRef({
    user: user?.user ?? '',
    uid: study.uid,
    site: study.site ?? study.siteId ?? '',
  });
  useEffect(() => {
    closeParamsRef.current = {
      user: user?.user ?? '',
      uid: study.uid,
      site: study.site ?? study.siteId ?? '',
    };
  }, [user?.user, study.uid, study.site, study.siteId]);

  // ── openStudy / closeStudy lifecycle ──────────────────────────────
  useEffect(() => {
    const stuKey = study.objKey;
    if (stuKey) {
      api.openStudy(stuKey).catch((err) => {
        pushError(`Failed to open study: ${String(err)}`);
      });
    }

    return () => {
      const p = closeParamsRef.current;
      if (p.user && p.uid && p.site) {
        api.closeStudy(p).catch(() => {
          /* best-effort close on unmount */
        });
      }
    };
  }, [study.objKey, study.uid]);

  // beforeunload — ensure closeStudy fires if the tab/window is closed
  useEffect(() => {
    const handleUnload = () => {
      const p = closeParamsRef.current;
      if (!p.user || !p.uid || !p.site) return;
      // sendBeacon for best-effort close — fire-and-forget JSON-RPC
      const payload = JSON.stringify({
        jsonrpc: '2.0',
        method: 'doCloseStudy',
        params: { user: p.user, uid: p.uid, site: p.site },
        id: Date.now(),
      });
      navigator.sendBeacon('/JSONRPC', payload);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // ── Auto-open child viewer windows based on monitor config ────────
  const monitorConfigs = useMonitorConfigStore((s) => s.configs);
  const autoOpenedRef = useRef<string | null>(null);

  useEffect(() => {
    // Only auto-open once per study (avoid re-opening if configs change)
    if (autoOpenedRef.current === study.uid) return;
    if (!capability.available) return;

    // Find configs with viewer roles that need a child window
    const viewerRoles: Array<'secondary-viewer' | 'primary-viewer' | 'prior-viewer'> = [
      'secondary-viewer',
      'prior-viewer',
    ];
    const toOpen = monitorConfigs.filter(
      (c) => viewerRoles.includes(c.role as typeof viewerRoles[number]),
    );
    if (toOpen.length === 0) return;

    const rawScreens = manager.getScreens();
    if (rawScreens.length < 2) return;

    autoOpenedRef.current = study.uid;

    for (const config of toOpen) {
      // Don't re-open if a window with this role already exists
      if (manager.getWindowForRole(config.role)) continue;

      const screen = findScreenByLabel(rawScreens, config.screenLabel);
      if (!screen) continue;

      openViewerOnScreen(screen, study.uid, siteId, config.role, viewer.id, study.objKey);
    }
  }, [study.uid, study.objKey, capability.available, monitorConfigs, manager, openViewerOnScreen, siteId, viewer.id]);

  // ── Close handler — calls api.closeStudy then parent onClose ──────
  const handleClose = useCallback(() => {
    const p = closeParamsRef.current;
    if (p.user && p.uid && p.site) {
      api.closeStudy(p).catch(() => {});
    }
    onClose();
  }, [onClose]);

  // ── Prior studies / comparisons ────────────────────────────────────
  const { data: comparisons } = useQuery({
    queryKey: queryKeys.comparisons(study.objKey),
    queryFn: () => api.getComparisons(study.objKey),
    enabled: priorsOpen && !!study.objKey,
    staleTime: 60_000,
  });
  const priorStudies = useMemo(() => comparisons?.records ?? [], [comparisons]);

  // ── Phase 3: Distribute protocol to child windows ─────────────
  useEffect(() => {
    if (!bestProtocol || childWindows.length === 0) return;

    // Determine prior study UID (first comparison if available)
    const priorUID = priorStudies.length > 0 ? priorStudies[0].uid : undefined;

    if (isMultiScreenBritProtocol(bestProtocol)) {
      // Multi-screen protocol — distribute per-screen layouts to child windows
      const assignments = distributeProtocol(
        bestProtocol,
        childWindows,
        study.uid,
        priorUID,
      );
      for (const assignment of assignments) {
        manager.sendProtocolToWindow(
          assignment.windowId,
          assignment.studyUID,
          assignment.ohifProtocol,
        );
      }
    } else {
      // Single-screen protocol — send the same protocol + study to all
      // viewer child windows so they mirror the primary layout.
      const ohifProtocol = britToOhifProtocol(bestProtocol);
      for (const child of childWindows) {
        if (child.role === 'prior-viewer' && priorUID) {
          // Prior-viewer gets the comparison study with the same layout
          manager.sendProtocolToWindow(child.windowId, priorUID, ohifProtocol);
        } else if (child.role !== 'worklist') {
          // Secondary-viewer (or primary-viewer) gets the current study
          manager.sendProtocolToWindow(child.windowId, study.uid, ohifProtocol);
        }
      }
    }
  }, [bestProtocol, childWindows, study.uid, priorStudies, manager]);

  const openDynamicTab = useUIStore((s) => s.openDynamicTab);
  const handleOpenPrior = useCallback(
    (prior: Study) => {
      openDynamicTab({
        id: `viewer-${prior.uid}`,
        label: `${prior.name || 'Study'} - ${prior.acc || prior.uid}`.slice(0, 40),
        type: 'viewer',
        study: prior,
        closeable: true,
      });
    },
    [openDynamicTab],
  );

  // ── Report panel resizable splitter ────────────────────────────────
  const { size: reportHeight, splitterProps } = useResizableSplitter({
    direction: 'vertical',
    initialSize: 200,
    minSize: 80,
    maxSize: 500,
  });

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1a1a2e',
      }}
    >
      {/* ── Title bar ────────────────────────────────────────────── */}
      <Box
        sx={{
          height: 32,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          backgroundColor: gwtStyles.mainView,
          borderBottom: '1px solid',
          borderColor: 'divider',
          gap: 0.5,
        }}
      >
        <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, flex: 1 }}>
          Viewer: {study.name} — {study.acc} — {study.mod}
        </Typography>

        {/* Pop out to secondary monitor */}
        {capability.available && (
          <PopOutButton
            screens={screens}
            studyInstanceUID={study.uid}
            siteId={siteId}
            onOpenOnScreen={openViewerOnScreen}
          />
        )}

        {/* Toggle prior studies sidebar */}
        <Tooltip title="Prior Studies">
          <IconButton
            size="small"
            onClick={() => setPriorsOpen((p) => !p)}
            sx={{ width: 24, height: 24, color: priorsOpen ? 'primary.main' : undefined }}
          >
            <PriorIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>

        {/* Toggle report panel */}
        <Tooltip title="Report Panel">
          <IconButton
            size="small"
            onClick={() => setReportOpen((p) => !p)}
            sx={{ width: 24, height: 24, color: reportOpen ? 'primary.main' : undefined }}
          >
            <ReportIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>

        {/* Open in new tab */}
        <Tooltip title="Open in OHIF (new tab)">
          <IconButton
            size="small"
            onClick={() => window.open(ohifUrl, '_blank')}
            sx={{ width: 24, height: 24 }}
          >
            <LaunchIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>

        {/* Close viewer */}
        <Tooltip title="Close viewer">
          <IconButton size="small" onClick={handleClose} sx={{ width: 24, height: 24 }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Main content: iframe + optional priors sidebar ───────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* OHIF iframe */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {!iframeLoaded && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
          <iframe
            ref={iframeRef}
            src={ohifUrl}
            title={`Viewer: ${study.name}`}
            onLoad={() => setIframeLoaded(true)}
            // Cross-Origin Isolation: OHIF v3 needs SharedArrayBuffer for
            // cornerstone3D rendering. The 'credentialless' attribute lets
            // the iframe achieve cross-origin isolation independently.
            // @ts-expect-error credentialless is a valid HTML attribute but not yet in React types
            credentialless="true"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
            }}
          />
        </Box>

        {/* ── Prior Studies sidebar ────────────────────────────── */}
        <Collapse in={priorsOpen} orientation="horizontal">
          <Box
            sx={{
              width: 220,
              height: '100%',
              borderLeft: '1px solid',
              borderColor: 'divider',
              backgroundColor: gwtStyles.mainView,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: 28,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                px: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: 11, fontWeight: 600, flex: 1 }}>
                Prior Studies ({priorStudies.length})
              </Typography>
              <IconButton size="small" onClick={() => setPriorsOpen(false)} sx={{ width: 20, height: 20 }}>
                {priorsOpen ? <ExpandMore sx={{ fontSize: 14 }} /> : <ExpandLess sx={{ fontSize: 14 }} />}
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {priorStudies.length === 0 ? (
                <Typography variant="caption" sx={{ p: 1, color: 'text.secondary', fontSize: 11 }}>
                  No prior studies found.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {priorStudies.map((prior) => (
                    <ListItemButton
                      key={prior.uid}
                      onClick={() => handleOpenPrior(prior)}
                      sx={{ py: 0.5, px: 1 }}
                    >
                      <ListItemText
                        primary={`${prior.date || '—'} · ${prior.mod || '—'}`}
                        secondary={prior.descr || prior.acc}
                        primaryTypographyProps={{ fontSize: 11, fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: 10 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* ── Report panel (collapsible below viewer) ──────────────── */}
      {reportOpen && (
        <>
          <Box
            {...splitterProps}
            sx={{
              height: 5,
              flexShrink: 0,
              cursor: 'row-resize',
              backgroundColor: 'divider',
              '&:hover': { backgroundColor: 'primary.light' },
            }}
          />
          <Box sx={{ height: reportHeight, flexShrink: 0, overflow: 'hidden' }}>
            <ReportViewer study={study} />
          </Box>
        </>
      )}
    </Box>
  );
};

function buildAutoViewerProtocol(study: Study, objects: DicomObject[] | undefined): BritProtocol | null {
  if (!objects || objects.length === 0) return null;

  const grouped = new Map<string, DicomObject[]>();
  for (const object of objects) {
    if (!object.seruid) continue;
    const list = grouped.get(object.seruid) ?? [];
    list.push(object);
    grouped.set(object.seruid, list);
  }

  const ranked = Array.from(grouped.entries())
    .map(([seriesUID, seriesObjects]) => {
      const representative = seriesObjects[Math.floor(seriesObjects.length / 2)] ?? seriesObjects[0];
      const description = String(representative.details?.sdes ?? '').toLowerCase();
      const isScout = description.includes('scout') || description.includes('localizer') || description.includes('topogram');
      return {
        seriesUID,
        count: seriesObjects.length,
        description,
        isScout,
      };
    })
    .sort((left, right) => {
      if (left.isScout !== right.isScout) return left.isScout ? 1 : -1;
      return right.count - left.count;
    });

  const selected = ranked[0];
  if (!selected) return null;

  const preset = getAutoWindowLevelPreset(study.mod, selected.description);
  const targetWindow: BritWindowContent = {
    offset: Math.max(0, Math.floor(selected.count / 2) - 1),
    content: {
      ctype: 'SERIES',
      name: 'Preferred Series',
      identifiers: [{ type: 'SERIESUID', val: selected.seriesUID }],
    },
  };
  if (preset) {
    targetWindow.window = preset.windowWidth;
    targetWindow.level = preset.windowCenter;
  }

  return {
    id: `auto-${study.uid}`,
    name: 'Auto Preferred Series',
    scope: 'user',
    screens: ['READING1'],
    content: {
      name: 'Auto Preferred Series',
      elements: [
        {
          ctype: 'STUDY',
          name: 'Target Study',
          identifiers: [{ type: 'MODALITY', val: study.mod }],
        },
      ],
    },
    steps: [
      {
        name: 'Auto Preferred Series',
        screens: {
          READING1: {
            views: [
              {
                role: 'TARGET',
                rows: 1,
                cols: 1,
                wins: [targetWindow],
              },
            ],
          },
        },
      },
    ],
  };
}

interface AutoWindowLevel {
  seriesInstanceUID: string;
  windowCenter: number;
  windowWidth: number;
}

function buildAutoWindowLevels(study: Study, objects: DicomObject[] | undefined): AutoWindowLevel[] {
  if (!objects || objects.length === 0) return [];

  const grouped = new Map<string, DicomObject[]>();
  for (const object of objects) {
    if (!object.seruid) continue;
    const list = grouped.get(object.seruid) ?? [];
    list.push(object);
    grouped.set(object.seruid, list);
  }

  return Array.from(grouped.entries())
    .map(([seriesInstanceUID, seriesObjects]) => {
      const representative = seriesObjects[Math.floor(seriesObjects.length / 2)] ?? seriesObjects[0];
      const description = String(representative.details?.sdes ?? '');
      const preset = getAutoWindowLevelPreset(study.mod, description);
      if (!preset) return null;
      return {
        seriesInstanceUID,
        windowCenter: preset.windowCenter,
        windowWidth: preset.windowWidth,
      };
    })
    .filter((item): item is AutoWindowLevel => item !== null);
}

function getAutoWindowLevelPreset(modality: string | undefined, description: string | undefined): { windowCenter: number; windowWidth: number } | null {
  const mod = String(modality ?? '').toUpperCase();
  const desc = String(description ?? '').toLowerCase();

  if (mod !== 'CT') return null;
  if (desc.includes('scout') || desc.includes('localizer') || desc.includes('topogram')) return null;
  if (desc.includes('lung')) return { windowCenter: -600, windowWidth: 1500 };
  if (desc.includes('mediast') || desc.includes('soft tissue')) return { windowCenter: 40, windowWidth: 350 };
  if (desc.includes('bone') || desc.includes('osseous')) return { windowCenter: 500, windowWidth: 2000 };
  if (desc.includes('brain') || desc.includes('head')) return { windowCenter: 40, windowWidth: 80 };
  return { windowCenter: 40, windowWidth: 400 };
}
